import { ChevronDown, ChevronUp, Smartphone, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { SensorPlanView, type HeadingBand, type PlanTarget } from './sensor-plan-view';
import { SensorRotationView, TILT_CHANNEL, type RotationBand } from './sensor-rotation-view';
import { SensorSourceSwitch } from './sensor-simulate-bar';
import { useSensorSimulator, type SimulatedSignal } from './sensor-simulator-context';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';

import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { SegmentedControl } from '@/components/ui/segmented-control';

/**
 * The task's one device, as a panel.
 *
 * Replaces the per-block simulate strips in Preview. Those are right in the EDITOR — a teacher
 * testing one block wants that block's own scenarios — and wrong here, where three strips model
 * three phones. This is one phone: a place, a bearing, and how well it is receiving.
 *
 * There is no scenario dropdown, and no coordinate entry. A teacher should not have to know a
 * latitude, and does not need to: every target is already authored, so the map can draw them and
 * the pin is dragged towards one.
 *
 * There are no preset chips either. An earlier version offered "At Site A", "Edge of Site A" and "Just
 * Outside Site A" per target as shortcuts into the continuous space; they came out on use. Dragging
 * a pin towards a target you can see, on a grid that states the distance, IS the short path — and a
 * dozen chips under the map cost more room and more reading than the drag they were saving.
 */

/** `EditorSectionHeading`'s treatment, without its top rule — the sections here are separated by
 *  spacing rather than by lines, and three rules in a phone sheet reads as a form, not a panel. */
function SectionHeading({ label }: { label: string }) {
  return <p className='mb-1.5 text-sm font-semibold uppercase tracking-wide text-ws-primary'>{label}</p>;
}

/**
 * A channel no block in this task reads.
 *
 * Shown rather than hidden, so the panel is the same shape every time and a teacher who has never
 * used a compass block learns that one exists. Collapsed to a single row rather than a dead
 * full-size widget, because three live-sized dials in a phone sheet is a lot of nothing.
 *
 * The reason is not decoration: a disabled control with no stated reason is the one case a
 * screen-reader user cannot work out, which is the same note `AddRowButton`'s `describedBy` carries.
 */

/**
 * The bar's toggle, which also says which source is live.
 *
 * `InputBlockFrame`'s settings control is the pattern: `aria-expanded`, the label with a chevron
 * that flips, and the `ws-in-progress` colours while it is open. Colour is already spent on OPEN,
 * so the mode has to be carried in TEXT — and the earlier lesson from the DataViz landmarks applies
 * here too: an icon that only reads once you already know is not a label.
 *
 * The mode is `shrink-0` and the panel's name is what truncates. On a 390px phone the bar leaves
 * this control very little, and "Phone Sensors" losing its tail costs nothing next to losing the
 * one word that says whether a real sensor is being read.
 *
 * Lives here rather than in the page because it reads the shared device, and the page is the
 * component that mounts the provider — a hook called there would see nothing.
 */
export function SensorSimulatorToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const sim = useSensorSimulator();
  const phone = useIsPhone();
  const mode = t(sim?.useDevice ? 'blocks.sensorSource.device' : 'blocks.sensorSource.simulate');
  return (
    <Button
      size='md'
      /**
       * `pressed`, which is the same treatment Variables and Flow carry in this bar — tinted while
       * available, filled while their panel is open. This was hand-rolled with `rounded-lg` and its
       * own `ws-in-progress` tint, so it sat beside them looking like a third kind of control.
       */
      pressed={open}
      data-sensor-sim-toggle=''
      onClick={onToggle}
      aria-expanded={open}
      /**
       * On a phone the NAME goes and the mode stays.
       *
       * The bar leaves this control about 170px once Reroll, Reset and Submit have taken theirs,
       * and "Phone Sensors · Simulate" needs half again that. Dropping the mode instead was the
       * other option and it is the wrong one: the panel's name is discoverable by opening it, and
       * whether a real sensor is being read is not. The icon is a phone and the label says the
       * source, which together read as well as the full string does.
       *
       * A symbol for the mode was considered and rejected for the reason the DataViz landmarks
       * settled: a glyph that only reads once you already know is not a label. `aria-label` carries
       * the whole thing regardless, so nothing is lost to a listener.
       */
      aria-label={`${t('task.sensorSim.pill')}, ${mode}`}
      icon={<Smartphone aria-hidden='true' />}
      iconTrailing={open
        ? <ChevronDown aria-hidden='true' />
        : <ChevronUp aria-hidden='true' />}
      className='min-w-0 shrink-0'
    >
      {!phone && <span aria-hidden='true' className='truncate'>{t('task.sensorSim.pill')}</span>}
      {/* Same words as the switch inside the panel, deliberately: the button is reporting that
          control, so borrowing its vocabulary is what makes the two read as one thing. */}
      <span aria-hidden='true' className='shrink-0 text-ws-muted'>
        {phone ? mode : `\u00b7 ${mode}`}
      </span>
    </Button>
  );
}

export function SensorSimulatorSheet({ targets, bands, rotationBands, tiltBands, hasGps, hasCompass, hasRotation, hasTilt, onClose }: {
  targets: PlanTarget[];
  bands: HeadingBand[];
  rotationBands: RotationBand[];
  tiltBands: RotationBand[];
  hasGps: boolean;
  hasCompass: boolean;
  hasRotation: boolean;
  hasTilt: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const sim = useSensorSimulator();
  const phone = useIsPhone();
  const landscape = useIsPhoneLandscape();
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus lands inside on open, and Escape closes — the minimum a dialog owes a keyboard user.
  /**
   * Focus in on open, Escape out, and a click anywhere outside closes it.
   *
   * `mousedown` on `document`, matching `InputBlockFrame`'s settings drawer exactly, because that
   * drawer is the precedent for "this opens a panel of things you adjust" and two panels closing on
   * two different events is how one ends up behaving subtly differently from the other.
   *
   * The outside-click half is what made the first version read as a bespoke overlay: it demanded
   * the X, where every other panel in the editor closes when you go back to work.
   */
  /**
   * Escape, the X, AND an outside click.
   *
   * The outside click was removed once, for a good reason that has since expired. Driving a
   * sensor is a conversation with the task — set the arrangement, calibrate on the block, turn
   * the dial, capture — and closing on every one of those presses made the sequence open, set,
   * close, calibrate, open, turn, close, capture. Reported, fairly, as hard to operate.
   *
   * What changed: the panel now covers the task it is describing, so leaving it open hides the
   * results the teacher opened it to watch. Reported, as fairly, the other way round. Dismissing
   * on a click outside is the lesser cost of the two, because reopening is one press and seeing
   * through the panel is not possible at all.
   *
   * ON MOUSEDOWN **AND** MOUSEUP, both outside. A press that starts inside and releases outside
   * is a drag of the heading dial or the map pin that happened to end past the edge, and closing
   * on that would undo the very gesture being made — see the Firefox retarget note in
   * `useArmedDelete`'s neighbours, which is the same mistake in a different control.
   */
  useEffect(() => {
    /* WHERE TO PUT FOCUS BACK. Captured before the panel takes it, because afterwards the
       answer is the panel. Without this, closing dropped focus to `<body>` and a keyboard user
       started again from the top of the page — the same loss the verdict dialog had. */
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    let startedOutside = false;
    const outside = (e: Event) => {
      const panel = panelRef.current;
      return !!panel && e.target instanceof Node && !panel.contains(e.target);
    };
    const onDown = (e: PointerEvent) => { startedOutside = outside(e); };
    const onUp = (e: PointerEvent) => {
      if (startedOutside && outside(e)) onClose();
      startedOutside = false;
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointerup', onUp, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointerup', onUp, true);
      /* Only if the panel still holds it: a close caused by clicking something else must not
         yank focus away from whatever the teacher just reached for. */
      if (panelRef.current?.contains(document.activeElement)) opener?.focus?.();
    };
  }, [onClose, phone]);

  if (!sim) return null;

  const signalOptions: SimulatedSignal[] = ['good', 'poor', 'none'];
  const signalLabel = (s: SimulatedSignal) => t(
    s === 'good' ? 'task.sensorSim.signalGood' : s === 'poor' ? 'task.sensorSim.signalPoor' : 'task.sensorSim.signalNone',
  );

  const body = (
    <div
      ref={panelRef}
      tabIndex={-1}
      role='dialog'
      /*
       * FALSE ON EVERY SIZE, and it used to be `true` on a phone.
       *
       * `aria-modal='true'` tells assistive tech that everything outside is inert, and nothing
       * here makes that so: Tab walks straight out, and the panel DISMISSES on an outside
       * press, which is the opposite of inert. On a phone it covers the task, which is what
       * the claim was reaching for, but covering is not trapping and a reader is not looking
       * at the screen.
       *
       * Making the claim true would mean a second focus trap beside `ModalShell`'s, which is
       * not exported and should not be copied. A dismissible panel that names itself and
       * closes on Escape is a correct non-modal dialog, so this says so.
       */
      aria-modal='false'
      aria-label={t('task.sensorSim.title')}
      /**
       * The Options flyout's own language: `border-ws-border-strong`, `px-4 py-3.5`, a column with
       * `gap-3.5`, and an OPAQUE `bg-ws-elevated` — the inline drawer's 40%-alpha tint is fine over
       * a card and not over a task with a map behind it.
       *
       * Opens upward, because the trigger is in the bottom bar. One layout rather than a phone
       * branch: a panel rising from the bottom bar IS a bottom sheet on a phone, and the width
       * clamps to the viewport instead of committing to 26rem on a 390px screen.
       */
      className={phone
        /**
         * The block editors' own full-screen shell — `usePortal` in `dataviz-block`, `fullScreen`
         * in `InputBlockFrame`. A flyout rising from the bottom bar is wrong on a phone in a way
         * the desktop case hides: it has to stay short enough to clear the bar, so the map gets a
         * few hundred pixels of a screen with room for all of it, and the panel sits on top of
         * the task it describes with the bar still competing underneath.
         *
         * `h-[100dvh]`, not `100vh` — the dynamic unit is what survives a phone's address bar
         * sliding away, which is why both of those two use it.
         */
        ? 'fixed inset-x-0 top-0 z-[130] flex h-[100dvh] flex-col gap-3.5 overflow-y-auto bg-ws-surface px-4 py-3.5'
        /* Centred, and free to be: the angle channel sets up a PLAY that runs on Calibrate, so
           nothing here has to be watched while the block is worked. It was briefly shifted left to
           keep the task reachable, which fixed a laptop and did nothing for a phone.

           `max-h-[75dvh]` and a tighter `gap-3`: at 80dvh with the channels stacked it ran off the
           top of a laptop screen, and a panel whose own heading has scrolled away is one you
           cannot tell is a panel. 75 rather than 70 now that the map compacts whenever a dial is
           stacked under it -- measured, a GPS + compass + tilt panel is 518px, so 75dvh clears it
           from a 700px window up where 70dvh needed 740. Below that it still scrolls, because
           518px of content does not fit in 392px and no cap pretends otherwise. */
        : 'absolute bottom-full left-1/2 z-[110] mb-2 flex max-h-[75dvh] w-[min(26rem,calc(100vw-1.5rem))] -translate-x-1/2 flex-col gap-3 overflow-y-auto rounded-xl border border-ws-border-strong bg-ws-elevated px-4 py-3 shadow-lg'}
    >
      <div className='flex items-center gap-2'>
        <Smartphone aria-hidden='true' className='h-4 w-4 text-ws-primary dark:text-ws-text-link' />
        <h2 className='flex-1 text-sm font-semibold text-ws-text'>{t('task.sensorSim.title')}</h2>
        <IconButton size='sm' onClick={onClose} aria-label={t('task.sensorSim.close')}>
          <X aria-hidden='true' className='h-4 w-4' />
        </IconButton>
      </div>

      {/*
        Device or Simulate, in the same switch the per-block strips use.

        Replaces a note explaining what the panel was for. The note said it in words; the switch
        says it by being there — and it is the one control here that decides whether anything below
        it applies at all, so it belongs at the top rather than described from it.
      */}
      <div className='flex items-center justify-center gap-1.5'>
        <SensorSourceSwitch
          mode={sim.useDevice ? 'device' : 'simulate'}
          onModeChange={next => sim.setUseDevice(next === 'device')}
          deviceLabel={t('blocks.sensorSource.device')}
          simulateLabel={t('blocks.sensorSource.simulate')}
        />
      </div>

      {/*
        The pin DRAWS whenever either channel is live, and that is not a convenience.

        `computeTrueHeading` takes a latitude and longitude to work out magnetic declination, so a
        compass block set to true north consumes a position even in a task with no GPS block in it.
        Hiding the map for a compass-only task would leave that correction with nothing to correct
        against — which is a mistake two separate widgets would have made silently.
      */}
      {/*
        Landscape splits the width and caps the map's height.

        The canvas is normally 0.72 of its own width, which on an 844x390 screen asks for a 590px
        map on a 390px viewport — so it overflowed and everything under it scrolled away. Filling
        the row's height instead gives a wide, short map, which is the shape of the screen.

        The other half is deliberately EMPTY, and reserved: the angle simulator goes there. A
        placeholder with a border or a label would be chrome standing in for a feature, and the
        blank half says what it is by being beside the map — the same reasoning the bottom bar's own
        reserved spacer carries.
      */}
      <div className={landscape ? 'flex min-h-0 flex-1 gap-3.5' : 'contents'}>
      {(hasGps || hasCompass) && (
        <SensorPlanView
          targets={targets}
          bands={bands}
          position={sim.position}
          heading={sim.heading.degrees}
          onPositionChange={at => sim.setPosition({ ...sim.position, ...at })}
          onHeadingChange={deg => sim.setHeading({ ...sim.heading, degrees: deg })}
          // Back to the first authored target, pointing north — the same state the provider
          // seeded on open, so Reset means "as it was" rather than "somewhere arbitrary".
          onReset={() => {
            if (targets[0]) sim.setPosition({ ...sim.position, ...targets[0].at });
            sim.setHeading({ ...sim.heading, degrees: 0 });
          }}
          showPin={hasGps || hasCompass}
          showRing={hasCompass}
          disabled={sim.useDevice}
          fillHeight={landscape}
          /*
            Shorter when an angle dial is stacked under it; see `compact`.

            `hasTilt` as well as `hasRotation`, and that was the bug: Tilt's channel draws THE
            SAME DIAL (see its note below -- a second one was built for Tilt and deleted), but
            this condition was written when Rotation was the only thing that could be under the
            map. So a GPS + compass + tilt task stacked a full-height 275px map on a 174px dial
            and overflowed the panel's own maximum by more than 200px, which is precisely what
            `compact`'s own note says it exists to prevent.
          */
          compact={!landscape && (hasRotation || hasTilt)}
        />
      )}
      {/*
        The half that was reserved, now filled.

        The map answers "where is the phone and which way is it pointing"; this answers "how far has
        it been turned, and from what posture". It emits an ATTITUDE rather than an angle, which the
        angle block runs through the same tracker a real accelerometer feeds — so the 180-degree
        fold, the six-degree latch and the off-plane check are derived here rather than declared.
        The old per-block strip returned a number, which meant the one thing the tracker exists for
        could not be tested at all.
      */}
      {hasRotation && (
        <SensorRotationView
          bands={rotationBands}
          attitude={sim.attitude}
          onAttitudeChange={sim.setAttitude}
          disabled={sim.useDevice}
          fillHeight={landscape}
        />
      )}
      {/* THE SAME DIAL Rotation uses, with Tilt's own bands shaded on it. A second dial was
          built for Tilt and then deleted: the circle already worked, it is the thing a teacher
          has learned to drive, and keeping one means a fix reaches both. */}
      {hasTilt && (
        <SensorRotationView
          channel={TILT_CHANNEL}
          bands={tiltBands}
          attitude={sim.attitude}
          onAttitudeChange={sim.setAttitude}
          disabled={sim.useDevice}
          fillHeight={landscape}
        />
      )}
      {landscape && !hasRotation && !hasTilt && <div className='min-w-0 flex-1' aria-hidden='true' />}
      </div>

      {/*
        A condition is not a position, so it does not belong on the map: no amount of dragging says
        the signal dropped. This is where the per-block fault scenarios went — Low Accuracy,
        Network-Based, No Reading — lifted to the device they were always describing.
      */}
      <div>
        <SectionHeading label={t('task.sensorSim.signalLabel')} />
        {/* A single-select, so it is the app's `SegmentedControl` rather than three hand-rolled
            buttons: that brings the roving tabindex and Arrow-key selection a radiogroup owes a
            keyboard user, which the hand-rolled version did not have. */}
        {/* Disabled on Device for the same reason the map is: a simulated signal quality has
            nothing to say about a real fix. `SegmentedControl` greys the whole switch rather than
            individual segments, which is right — this is one choice, and if it cannot be made then
            none of its segments can. */}
        <SegmentedControl
          options={signalOptions.map(sg => ({ id: sg, label: signalLabel(sg) }))}
          value={sim.signal}
          onChange={v => sim.setSignal(v as SimulatedSignal)}
          aria-label={t('task.sensorSim.signalLabel')}
          disabled={sim.useDevice}
          stretch
        />
      </div>
    </div>
  );

  // Portalled on a phone so the overlay escapes the bar's own stacking context; in place otherwise,
  // where `absolute` positions it against the bar slot it belongs to.
  return phone ? createPortal(body, document.body) : body;
}
